import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { loginWithCode } from '../services/authService';

/**
 * Landing page for the welcome WhatsApp link (/i/:code). Redeems the single-use code:
 *  - lead/provisioned accounts -> reset-password page to set their own password
 *  - everyone else            -> logged in, sent to upcoming lessons
 * A spent/expired code shows a friendly message pointing to normal login.
 */
export default function LoginLink() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  // The token is single-use, so guard against React StrictMode's double-invoke in dev,
  // which would redeem it twice and make the second attempt fail.
  const redeemed = useRef(false);

  useEffect(() => {
    if (redeemed.current) return;
    redeemed.current = true;

    (async () => {
      const result = await loginWithCode(code);
      if (result.success && result.requiresPasswordSet) {
        navigate(`/reset-password/${result.resetToken}`, { replace: true });
      } else if (result.success) {
        navigate('/upcoming-lessons', { replace: true });
      } else {
        setFailed(true);
      }
    })();
  }, [code, navigate]);

  if (failed) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: 24, textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
        <h2>Log in to continue</h2>
        <p>
          Please click the login button and log in with your email and password, or{' '}
          <Link to="/forgot-password">reset your password</Link> if you've forgotten it.
        </p>
        <Link
          to="/login"
          className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded"
          style={{ textDecoration: 'none', marginTop: 8 }}
        >
          Log in
        </Link>
        <p style={{ marginTop: 16, fontSize: 14, color: '#6b7280' }}>
          Your login link has expired — it can only be used once.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '80px auto', padding: 24, textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
      <p>Logging you in…</p>
    </div>
  );
}
